import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';

import { AuthService } from '../../services/auth.service';
import { PinVaultService } from '../../services/pin-vault.service';
import { User } from '../../models/user.model';

export interface PinState {
  showPinDialog: boolean;
  pinDialogMode: 'setup' | 'reset';
  userHasPin: boolean;
  showPinChangeDialog: boolean;
  pinChangeMode: 'setup' | 'change';
  pinChangeStep: 'current' | 'new' | 'confirm';
  pinChangeDigits: string[];
  pinChangeError: string;
  currentUser: User | null;
}

export class TopbarPinCollab {

  private pinChangeCurrent = '';
  private pinChangeNew = '';

  constructor(
    private authService: AuthService,
    private pinVaultService: PinVaultService,
    private translate: TranslateService,
    private messageService: MessageService,
  ) {}

  async managePin(state: PinState, closeAll: () => void): Promise<void> {
    closeAll();
    const email = localStorage.getItem('currentUserEmail') || '';
    const hasVault = await this.pinVaultService.hasVault(email);
    state.pinDialogMode = hasVault ? 'reset' : 'setup';
    state.showPinDialog = true;
  }

  onPinSaved(state: PinState): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant('PIN.SETUP_SUCCESS') as string,
    });
    state.userHasPin = true;
  }

  openChangePinDialog(state: PinState): void {
    state.pinChangeMode = 'change';
    state.pinChangeStep = 'current';
    state.pinChangeDigits = [];
    state.pinChangeError = '';
    this.pinChangeCurrent = '';
    this.pinChangeNew = '';
    state.showPinChangeDialog = true;
  }

  openSetupPinDialog(state: PinState): void {
    state.pinChangeMode = 'setup';
    state.pinChangeStep = 'new';
    state.pinChangeDigits = [];
    state.pinChangeError = '';
    this.pinChangeCurrent = '';
    this.pinChangeNew = '';
    state.showPinChangeDialog = true;
  }

  closePinChangeDialog(state: PinState): void {
    state.showPinChangeDialog = false;
    state.pinChangeDigits = [];
    state.pinChangeError = '';
  }

  pinChangeEnterDigit(digit: string, state: PinState): void {
    if (state.pinChangeDigits.length >= 6) return;
    state.pinChangeDigits = [...state.pinChangeDigits, digit];
    if (state.pinChangeDigits.length === 6) {
      this.processPinChangeStep(state.pinChangeDigits.join(''), state);
    }
  }

  pinChangeDeleteDigit(state: PinState): void {
    if (state.pinChangeDigits.length > 0) {
      state.pinChangeDigits = state.pinChangeDigits.slice(0, -1);
      state.pinChangeError = '';
    }
  }

  private async processPinChangeStep(pin: string, state: PinState): Promise<void> {
    const email = state.currentUser?.email || localStorage.getItem('currentUserEmail') || '';

    if (state.pinChangeStep === 'current') {
      const result = await this.pinVaultService.unlock(email, pin);
      if (!result.data) {
        state.pinChangeError = this.translate.instant('PIN.WRONG') + (result.remaining > 0 ? ` (${result.remaining} ${this.translate.instant('PIN.ATTEMPTS_LEFT')})` : '');
        state.pinChangeDigits = [];
        if (result.remaining <= 0) {
          this.closePinChangeDialog(state);
          state.userHasPin = false;
        }
        return;
      }
      this.pinChangeCurrent = pin;
      state.pinChangeStep = 'new';
      state.pinChangeDigits = [];
      state.pinChangeError = '';
    } else if (state.pinChangeStep === 'new') {
      this.pinChangeNew = pin;
      state.pinChangeStep = 'confirm';
      state.pinChangeDigits = [];
      state.pinChangeError = '';
    } else if (state.pinChangeStep === 'confirm') {
      if (pin !== this.pinChangeNew) {
        state.pinChangeError = this.translate.instant('PIN.MISMATCH');
        state.pinChangeStep = 'new';
        this.pinChangeNew = '';
        state.pinChangeDigits = [];
        return;
      }

      const name = state.currentUser?.name || localStorage.getItem('currentUserName') || email;
      const companyId = localStorage.getItem('selectedCompanyId') || '';

      if (state.pinChangeMode === 'change') {
        const result = await this.authService.changePin(email, this.pinChangeCurrent, this.pinChangeNew);
        if (result.success) {
          this.messageService.add({ severity: 'success', summary: this.translate.instant('PIN.SETUP_SUCCESS') });
          state.userHasPin = true;
          this.closePinChangeDialog(state);
        } else {
          state.pinChangeError = result.error === 'wrong_pin' ? this.translate.instant('PIN.WRONG') : this.translate.instant('PIN.SETUP_ERROR');
          state.pinChangeDigits = [];
        }
      } else {
        const success = await this.authService.setPin(this.pinChangeNew, email, name, companyId);
        if (success) {
          this.messageService.add({ severity: 'success', summary: this.translate.instant('PIN.SETUP_SUCCESS') });
          state.userHasPin = true;
          this.closePinChangeDialog(state);
        } else {
          state.pinChangeError = this.translate.instant('PIN.SETUP_ERROR');
          state.pinChangeDigits = [];
        }
      }
    }
  }
}
